import styled, { css } from "styled-components";

import { frameFillBackground } from "components/common/backgroundFrame";
import { CSS_VARIABLES } from "enums/cssVariables";
import { textFieldFocus } from "globalStyles";

import {
    DistributionCaseProposalProps,
    DistributionCaseStyleProps,
    LabelGroupStyleProps,
    ProposalDetailStyleProps,
} from "../types";
import {
    VOTEBOX_BORDER_WIDTH,
    VOTEBOX_DISABLED_BORDER_COLOR,
    VOTEBOX_DISABLED_INPUT_BACKGROUND_COLOR,
    VOTEBOX_DISABLED_TEXT_COLOR,
    VOTEBOX_FONT_SIZE_BODY,
    VOTEBOX_FONT_SIZE_CAPTION,
    VOTEBOX_FONT_SIZE_INPUT,
    VOTEBOX_FONT_SIZE_SMALL,
    VOTEBOX_FONT_WEIGHT_BOLD,
    VOTEBOX_FONT_WEIGHT_MEDIUM,
    VOTEBOX_FONT_WEIGHT_REGULAR,
    VOTEBOX_FONT_WEIGHT_SEMIBOLD,
    VOTEBOX_INPUT_RADIUS,
    VOTEBOX_MUTED_BACKGROUND_COLOR,
    VOTEBOX_NEUTRAL_BORDER_COLOR,
    VOTEBOX_OPTION_RADIUS,
    VOTEBOX_ROW_GAP,
    VOTEBOX_SECONDARY_TEXT_COLOR,
    VOTEBOX_TEXT_COLOR,
    VOTEBOX_WHITE,
} from "../voteboxTokens";
import { getSelectionColor } from "../services/getSelectionColor";

export function getBoxSize({
    color,
    isCompact,
}: Pick<DistributionCaseProposalProps, "color" | "isCompact">) {
    if (isCompact) {
        return "small";
    }

    // Candidates get the same box as their basic options; deliberation options a smaller one.
    return color ? "medium" : "large";
}

// An option without voices that can no longer get any: after the vote is sent, or once all the
// voices are given.
function isMuted({
    $isActive,
    $isDisabled,
}: Pick<DistributionCaseStyleProps, "$isActive" | "$isDisabled">) {
    return $isDisabled && !$isActive;
}

function getBorderColor(props: DistributionCaseStyleProps) {
    if (props.$isActive) {
        return getSelectionColor(props.$color);
    }

    if (isMuted(props)) {
        return VOTEBOX_DISABLED_BORDER_COLOR;
    }

    return props.$color || VOTEBOX_NEUTRAL_BORDER_COLOR;
}

function getBackground(props: DistributionCaseStyleProps) {
    if (props.$isActive) {
        return getSelectionColor(props.$color);
    }

    if (isMuted(props)) {
        return VOTEBOX_MUTED_BACKGROUND_COLOR;
    }

    return `var(${CSS_VARIABLES.UNSELECTED_OPTION_BACKGROUND_COLOR}, transparent)`;
}

function getTextColor(props: DistributionCaseStyleProps) {
    if (props.$isActive) {
        return VOTEBOX_WHITE;
    }

    if (isMuted(props)) {
        return VOTEBOX_DISABLED_TEXT_COLOR;
    }

    return props.$color || VOTEBOX_TEXT_COLOR;
}

function getInputBorderColor(props: DistributionCaseStyleProps) {
    if (props.$isActive) {
        return VOTEBOX_WHITE;
    }

    if (isMuted(props)) {
        return VOTEBOX_DISABLED_BORDER_COLOR;
    }

    return VOTEBOX_SECONDARY_TEXT_COLOR;
}

// Once the vote is sent, the options with voices keep their look and the others are greyed,
// as the basic options.
export const StyledWrapper = styled.div<DistributionCaseStyleProps>`
    display: flex;
    flex-direction: ${(props) => (props.$isCompact ? "column" : "row")};
    flex: ${(props) => (props.$isCompact ? "1" : "none")};
    align-items: center;
    gap: ${(props) => (props.$isCompact ? "6px" : VOTEBOX_ROW_GAP)};
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    margin-bottom: 0.5rem;
    padding: ${(props) => (props.$isCompact ? "8px 6px" : "6px 6px 6px 14px")};
    border: ${VOTEBOX_BORDER_WIDTH} solid ${(props) => getBorderColor(props)};
    border-radius: ${VOTEBOX_OPTION_RADIUS};
    background: ${(props) => getBackground(props)};
    color: ${(props) => getTextColor(props)};
    font-weight: ${(props) =>
        props.$isActive
            ? VOTEBOX_FONT_WEIGHT_BOLD
            : VOTEBOX_FONT_WEIGHT_MEDIUM};
    cursor: ${(props) => (props.$isDisabled ? "not-allowed" : null)};
`;

// Side by side, the box sits next to the label, above the input, as in the mockup. When a label of
// the line no longer fits next to its box, every box of the line goes on top of its label, as for
// the basic options.
const compactLabelGroup = css<LabelGroupStyleProps>`
    flex-direction: ${(props) =>
        "top" === props.$boxPosition ? "column" : "row"};
    justify-content: center;
`;

export const LabelGroup = styled.div<LabelGroupStyleProps>`
    display: flex;
    flex: 1;
    align-items: center;
    align-self: stretch;
    gap: ${(props) => (props.$isCompact ? "4px" : "8px")};
    min-width: 0;
    ${(props) => props.$isCompact && compactLabelGroup}
`;

export const Input = styled.input<DistributionCaseStyleProps>`
    flex: none;
    box-sizing: border-box;
    width: ${(props) => (props.$isCompact ? "100%" : "88px")};
    height: 40px;
    padding: 0 10px;
    border: ${VOTEBOX_BORDER_WIDTH} solid
        ${(props) => getInputBorderColor(props)};
    border-radius: ${VOTEBOX_INPUT_RADIUS};
    background: ${VOTEBOX_WHITE};
    color: ${VOTEBOX_TEXT_COLOR};
    text-align: center;
    font-size: ${VOTEBOX_FONT_SIZE_INPUT};
    font-weight: ${VOTEBOX_FONT_WEIGHT_SEMIBOLD};
    /* Over a background image, a white input takes the frame background, with the entity opacity. */
    ${frameFillBackground}

    &:focus {
        ${textFieldFocus()}
    }

    &[disabled] {
        background: ${VOTEBOX_DISABLED_INPUT_BACKGROUND_COLOR};
        cursor: not-allowed;
    }

    /* Remove arrow */
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

export const StyledPropositionsText = styled.div`
    /* A word is only cut when it cannot fit alone on a line. */
    overflow-wrap: break-word;
`;

export const ProposalImage = styled.img<
    Pick<DistributionCaseStyleProps, "$isActive" | "$isCompact" | "$isDisabled">
>`
    flex: none;
    width: ${(props) => (props.$isCompact ? "40px" : "56px")};
    height: ${(props) => (props.$isCompact ? "40px" : "56px")};
    object-fit: cover;
    border-radius: ${VOTEBOX_OPTION_RADIUS};
    opacity: ${(props) => (isMuted(props) ? 0.6 : 1)};
`;

// Side by side, the label keeps its own width, so that the box and the label are centered together.
const compactText = css`
    flex: 0 1 auto;
    max-width: 100%;
`;

export const TextWrapper = styled.div<
    Pick<DistributionCaseStyleProps, "$isCompact">
>`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    min-width: 0;
    font-size: ${(props) =>
        props.$isCompact ? VOTEBOX_FONT_SIZE_SMALL : VOTEBOX_FONT_SIZE_BODY};
    text-align: ${(props) => (props.$isCompact ? "center" : "start")};
    white-space: normal;
    ${(props) => props.$isCompact && compactText}
`;

export const DescriptionText = styled.p<ProposalDetailStyleProps>`
    margin: 0;
    font-size: ${VOTEBOX_FONT_SIZE_CAPTION};
    font-weight: ${VOTEBOX_FONT_WEIGHT_REGULAR};
    color: ${(props) => {
        if (props.$isActive) {
            return VOTEBOX_WHITE;
        }

        if (props.$isDisabled) {
            return VOTEBOX_DISABLED_TEXT_COLOR;
        }

        return VOTEBOX_SECONDARY_TEXT_COLOR;
    }};
`;
